import {
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    ListCareLogsParams,
    UpsertCareLogRequest,
} from '@/lib/api/care-logs-api'
import { trackCareLogExport } from '@/hooks/use-care-log-list-tracking'
import { careLogsApi } from '@/lib/api/care-logs-api'
import {
    downloadBlob,
    filenameFromContentDisposition,
} from '@/lib/download-blob'
import { m } from '@/lib/paraglide/messages'

export const careLogKeys = {
    all: ['care-logs'] as const,
    lists: () => [...careLogKeys.all, 'list'] as const,
    list: (params: ListCareLogsParams) =>
        [...careLogKeys.lists(), params] as const,
    detail: (id: string) => [...careLogKeys.all, 'detail', id] as const,
}

export function useCareLogs(params?: ListCareLogsParams) {
    return useQuery({
        queryKey: careLogKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await careLogsApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

// useMyGrowers loads the households linked to the current account. A grower
// login uses this to pick which of its own households a log belongs to. Not
// called for admins (they pick from every household via GrowerSearchSelect).
export function useMyGrowers(enabled: boolean) {
    return useQuery({
        queryKey: [...careLogKeys.all, 'my-growers'] as const,
        queryFn: async () => {
            const res = await careLogsApi.myGrowers()
            return res.data ?? []
        },
        enabled,
    })
}

export function useCareLog(id: string | null) {
    return useQuery({
        queryKey: careLogKeys.detail(id ?? ''),
        queryFn: async () => {
            const res = await careLogsApi.getById(id as string)
            return res.data
        },
        enabled: !!id,
    })
}

const growerAreasQuery = (growerId: string) => ({
    queryKey: [...careLogKeys.all, 'grower-areas', growerId] as const,
    queryFn: async () => {
        const res = await careLogsApi.growerAreas(growerId)
        return res.data ?? []
    },
})

// useCareLogGrowerAreas loads a household's areas (vùng trồng) for the care-log
// parcel picker. Scoped server-side (admin any household, grower only its own).
export function useCareLogGrowerAreas(growerId: string | null) {
    return useQuery({
        ...growerAreasQuery(growerId ?? ''),
        enabled: !!growerId,
    })
}

// useCareLogAreasOfGrowers: vùng trồng của NHIỀU hộ cùng lúc (tài khoản hộ gắn
// với vài hộ). Dùng chung khoá cache với useCareLogGrowerAreas nên hộ nào đã tải
// ở form nhật ký thì bộ lọc có sẵn ngay. `lists` giữ đúng thứ tự `growerIds`;
// `isSuccess` chỉ true khi MỌI hộ đã tải xong (rỗng `growerIds` → true);
// `isFetching` true khi còn hộ nào đang tải/tải lại (kể cả làm mới cache cũ).
export function useCareLogAreasOfGrowers(growerIds: string[]) {
    return useQueries({
        queries: growerIds.map((id) => growerAreasQuery(id)),
        combine: (results) => ({
            lists: results.map((r) => r.data ?? []),
            isSuccess: results.every((r) => r.isSuccess),
            isFetching: results.some((r) => r.isFetching),
        }),
    })
}

// useCareLogAreaContext: hộ + vùng của một mã QR vùng trồng NẾU tài khoản được
// ghi nhật ký cho hộ đó. Ngoài phạm vi server trả 404 — đó là câu trả lời "không"
// chứ không phải sự cố, nên không retry. `null` = không hỏi (khách, thiếu quyền).
export function useCareLogAreaContext(areaId: string | null) {
    return useQuery({
        queryKey: [...careLogKeys.all, 'area-context', areaId ?? ''] as const,
        queryFn: async () => {
            const res = await careLogsApi.areaContext(areaId as string)
            return res.data ?? null
        },
        enabled: !!areaId,
        retry: false,
    })
}

export function useCreateCareLog() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertCareLogRequest) => careLogsApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: careLogKeys.lists() })
            toast.success(m.toast_care_log_save_success())
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.toast_care_log_save_error())
        },
    })
}

export function useUpdateCareLog(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpsertCareLogRequest) =>
            careLogsApi.update(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: careLogKeys.lists() })
            void qc.invalidateQueries({ queryKey: careLogKeys.detail(id) })
            toast.success(m.toast_care_log_save_success())
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.toast_care_log_save_error())
        },
    })
}

export function useDeleteCareLog() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => careLogsApi.delete(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: careLogKeys.lists() })
            toast.success(m.toast_care_log_delete_success())
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.toast_care_log_delete_error())
        },
    })
}

// Lỗi của request tải file là Blob chứ không phải JSON — phải đọc ra mới lấy
// được thông báo server (vd "vượt trần xuất, thu hẹp bộ lọc"), thay vì chỉ báo
// "thất bại" chung chung mà người dùng không biết sửa gì.
async function serverMessageFromBlobError(
    err: unknown,
): Promise<string | undefined> {
    const data = (err as { response?: { data?: unknown } } | null)?.response
        ?.data as { text?: unknown } | undefined
    // Duck-type thay vì `instanceof Blob`: chỉ cần đọc được thành chuỗi.
    if (!data || typeof data.text !== 'function') return undefined
    try {
        const body = JSON.parse(await (data as Blob).text()) as {
            error?: { message?: unknown }
        }
        const msg = body.error?.message
        return typeof msg === 'string' && msg ? msg : undefined
    } catch {
        return undefined
    }
}

// useExportCareLogs tải Excel nhật ký theo bộ lọc đang áp dụng ở màn danh sách.
export function useExportCareLogs() {
    return useMutation({
        mutationFn: async (params: ListCareLogsParams) => {
            const res = await careLogsApi.exportExcel(params)
            downloadBlob(
                res.data,
                filenameFromContentDisposition(
                    res.headers['content-disposition'] as string | undefined,
                ) ?? 'NHAT_KY_CHAM_SOC.xlsx',
            )
        },
        onSuccess: (_data, params) => trackCareLogExport(params, { ok: true }),
        onError: async (err, params) => {
            trackCareLogExport(params, { ok: false, error: err })
            toast.error(
                (await serverMessageFromBlobError(err)) ??
                    m.care_log_export_failed(),
            )
        },
    })
}
